import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, finalize, map, shareReplay, tap } from 'rxjs';
import { EventsService, Event as EventModel, EventDashboardStats } from '../events/events.service';
import { EventDetailsDialogComponent } from '../events/event-details-dialog.component';
import { EventFormDialogComponent } from '../events/event-form-dialog.component';
import { ConfirmDialogComponent } from '../events/confirm-dialog.component';

import { AuthService } from '../shared/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

type EventListMode = 'upcoming' | 'past';

@Component({
  selector: 'app-global-event-manager-dashboard',
  imports: [
    MatIconModule, MatTooltipModule, MatCardModule,
    MatProgressSpinnerModule, MatButtonModule, MatButtonToggleModule,
    MatPaginatorModule, AsyncPipe, DatePipe
  ],
  templateUrl: './global-event-manager-dashboard.component.html',
  styleUrl: './global-event-manager-dashboard.component.scss'
})
export class GlobalEventManagerDashboardComponent implements OnInit {
  private eventsService = inject(EventsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
private readonly authService = inject(AuthService);
 private readonly dialog = inject(MatDialog);
   private readonly snackBar = inject(MatSnackBar);

  // ---- Metrics ----
  private readonly statsLoadingSubject = new BehaviorSubject<boolean>(true);
  statsLoading$ = this.statsLoadingSubject.asObservable();

  stats$: Observable<EventDashboardStats> = this.eventsService.getDashboardStats().pipe(
    tap(() => this.statsLoadingSubject.next(true)),
    finalize(() => this.statsLoadingSubject.next(false)),
    shareReplay(1)
  );

  totalEvents$ = this.stats$.pipe(map(s => s.totalEvents));
  upcomingEvents$ = this.stats$.pipe(map(s => s.upcomingCount));
  pastEvents$ = this.stats$.pipe(map(s => s.pastCount));
  orgsWithEvents$ = this.stats$.pipe(map(s => s.totalOrganizationsWithEvents));
  virtualEvents$ = this.stats$.pipe(map(s => s.virtualEventCount));
  inPersonEvents$ = this.stats$.pipe(map(s => s.inPersonEventCount));

  // ---- Event list (upcoming / past toggle) ----
  listMode: EventListMode = 'upcoming';
  listLoading = false;

allUpcoming: EventModel[] = [];
  upcomingPageIndex = 0;
  upcomingPageSize = 5;
  pagedUpcoming: EventModel[] = [];

  pastEventsList: EventModel[] = [];
  pastPageIndex = 0;
  pastPageSize = 5;
  pastTotalCount = 0;

  ngOnInit() {
    this.stats$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.loadUpcoming();
  }

  switchToUpcoming() {
    this.listMode = 'upcoming';
    if (!this.allUpcoming.length) {
      this.loadUpcoming();
    }
  }

  switchToPast() {
    this.listMode = 'past';
    this.loadPast();
  }

  private loadUpcoming() {
  this.listLoading = true;
  this.eventsService.getUpcomingEvents(100)
    .pipe(finalize(() => this.listLoading = false))
    .subscribe({
      next: (events) => {
        this.allUpcoming = events ?? [];
        this.upcomingPageIndex = 0;
        this.applyUpcomingPage();
      },
      error: () => {
        this.allUpcoming = [];
        this.pagedUpcoming = [];
      }
    });
}
  private applyUpcomingPage() {
    const start = this.upcomingPageIndex * this.upcomingPageSize;
    this.pagedUpcoming = this.allUpcoming.slice(start, start + this.upcomingPageSize);
  }

  onUpcomingPage(event: PageEvent) {
    this.upcomingPageIndex = event.pageIndex;
    this.upcomingPageSize = event.pageSize;
    this.applyUpcomingPage();
  }

  private loadPast() {
    this.listLoading = true;
    this.eventsService.getPastEventsPaged(this.pastPageIndex + 1, this.pastPageSize)
      .pipe(finalize(() => this.listLoading = false))
      .subscribe({
        next: (res) => {
          this.pastEventsList = res.items ?? [];
          this.pastTotalCount = res.totalCount ?? 0;
        },
        error: () => {
          this.pastEventsList = [];
          this.pastTotalCount = 0;
        }
      });
  }

  onPastPage(event: PageEvent) {
    this.pastPageIndex = event.pageIndex;
    this.pastPageSize = event.pageSize;
    this.loadPast();
  }
 private canModify(): boolean {
    const user = this.authService.currentUserValue;
    const roles = user?.roles ?? [];

    return !!user?.isSuperAdmin || roles.includes('superadmin') || roles.includes('globaleventmanager');
  }
get isElevatedEventAccess(): boolean {
    const user = this.authService.currentUserValue;
    const roles = user?.roles ?? [];

    return !!user?.isSuperAdmin || roles.includes('superadmin') || roles.includes('globaleventmanager');
}

viewEvent(ev: EventModel): void {
    const modify = this.canModify();

    const dialogRef = this.dialog.open(EventDetailsDialogComponent, {
      width: '95vw',
      maxWidth: '1536px',
      maxHeight: '90vh',
      data: {
        event: ev,
        canModify: modify,
        isAuthenticated: true,
        isSuperAdmin: this.isElevatedEventAccess   // was: currentUserValue?.isSuperAdmin || false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'edit' && modify) {
        this.openEditDialog(result.event);
      } else if (result?.action === 'delete' && modify) {
        this.confirmDelete(result.event);
      }
    });
}

  private openEditDialog(ev: EventModel): void {
    const dialogRef = this.dialog.open(EventFormDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      data: {
        mode: 'edit',
        event: ev,
        organizationId: (ev as any).organizationId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.updateEvent(ev.id, result.data);
      }
    });
  }

  private updateEvent(id: string, data: Partial<EventModel>): void {
    this.eventsService.updateEvent(id, data).subscribe({
      next: () => {
        this.snackBar.open('Event updated successfully', 'Close', { duration: 3000 });
        this.loadUpcoming();
        this.loadPast();
      },
      error: () => {
        this.snackBar.open('Failed to update event', 'Close', { duration: 3000 });
      }
    });
  }

  private confirmDelete(ev: EventModel): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Event',
        message: `Are you sure you want to delete "${ev.title}"? This action cannot be undone.`
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteEvent(ev.id);
      }
    });
  }

  private deleteEvent(id: string): void {
    this.eventsService.deleteEvent(id).subscribe({
      next: () => {
        this.snackBar.open('Event deleted successfully', 'Close', { duration: 3000 });
        this.loadUpcoming();
        this.loadPast();
      },
      error: () => {
        this.snackBar.open('Failed to delete event', 'Close', { duration: 3000 });
      }
    });
  }

  manageEvents(): void {
    this.router.navigate(['/events']);
  }

createEvent(): void {
  const openDialog = (organizations: { id: string; name: string }[] = []) => {
    const dialogRef = this.dialog.open(EventFormDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      data: {
        mode: 'create',
        organizationId: this.authService.currentUserValue?.organizationId,
        organizations
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.eventsService.createEvent(result.data).subscribe({
          next: () => {
            this.snackBar.open('Event created successfully', 'Close', { duration: 3000 });
            this.loadUpcoming();
            this.loadPast();
          },
          error: () => {
            this.snackBar.open('Failed to create event', 'Close', { duration: 3000 });
          }
        });
      }
    });
  };

  this.eventsService.getOrganizationsLightweight()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (orgs) => openDialog(orgs),
      error: (err) => {
        console.error('Failed to load organizations:', err);
        openDialog(); // still open, just without a populated org selector
      }
    });
}
}